import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, where, orderBy, onSnapshot, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { Loader2, Key, User, X, Fingerprint, Trash2 } from 'lucide-react'; // Ajouté CheckCircle, Clock si vous voulez des icônes spécifiques pour les statuts

// Liste des rôles disponibles pour la modification (existante)
const AVAILABLE_ROLES = [
    { value: 'etudiant', label: 'Étudiant' },
    { value: 'enseignant', label: 'Enseignant' },
    { value: 'directeurPedagogique', label: 'Directeur pédagogique' },
    { value: 'admin', label: 'Administrateur d\'École' },
    { value: 'secretaireGeneral', label: 'Secrétaire Général' },
    { value: 'financier', label: 'Financier' },
    { value: 'parent', label: 'Parent' },
];

// NOUVELLE LISTE DES STATUTS DISPONIBLES
const AVAILABLE_STATUSES = [
    { value: 'active', label: 'Actif' },
    { value: 'toRenew', label: 'À Renouveler' },
    { value: 'inactive', label: 'Inactif' }, // Vous pouvez ajouter d'autres statuts si nécessaire
];

/**
 * Composant de gestion des rôles et statuts utilisateur.
 * Affiche la liste des utilisateurs de l'école et permet d'attribuer de nouveaux rôles, de modifier les statuts et de supprimer des comptes.
 * @param {object} props.userProfile - Profil de l'utilisateur connecté (rôle, schoolId, uid).
 * @param {string} props.selectedSchoolForSuperAdmin - ID de l'école sélectionnée par le Super Admin.
 * @param {function} props.setMessage - Fonction pour afficher les messages de succès.
 * @param {function} props.setError - Fonction pour afficher les messages d'erreur.
 */
const UserRoleManagement = ({ userProfile, selectedSchoolForSuperAdmin, setMessage, setError }) => {
    const db = getFirestore();

    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [updatingUserId, setUpdatingUserId] = useState(null); // Pour le rôle
    const [deletingUserId, setDeletingUserId] = useState(null);
    const [updatingStatusUserId, setUpdatingStatusUserId] = useState(null); // NOUVEL ÉTAT pour le statut
    const [currentError, setCurrentError] = useState(null);

    // --- 1. Récupération des utilisateurs via onSnapshot ---
    useEffect(() => {
        let unsubscribe = () => {};

        setLoadingUsers(true);
        setCurrentError(null);
        setUsers([]);

        const role = userProfile?.role;
        const uid = userProfile?.uid;
        const profileSchoolId = userProfile?.schoolId;

        console.log("UserRoleManagement: useEffect déclenché. Rôle:", role, "SchoolId du profil:", profileSchoolId, "SchoolId sélectionnée:", selectedSchoolForSuperAdmin);

        if (!uid || !role) {
            console.warn("UserRoleManagement: En attente des données de l'utilisateur actuel.");
            setLoadingUsers(false);
            return;
        }

        let schoolIdToFilter = null;

        if (role === 'superAdmin' && selectedSchoolForSuperAdmin) {
            schoolIdToFilter = selectedSchoolForSuperAdmin;
        } else if (['admin', 'secretaireGeneral', 'financier', 'directeurPedagogique'].includes(role) && profileSchoolId) { // Ajout de directeurPedagogique
            schoolIdToFilter = profileSchoolId;
        } else if (role === 'superAdmin' && !selectedSchoolForSuperAdmin) {
            setCurrentError("Super Admin : Veuillez sélectionner une école pour gérer les rôles et statuts.");
            setLoadingUsers(false);
            return;
        } else {
            const isAuthorizedRole = ['superAdmin', 'admin', 'secretaireGeneral', 'financier', 'directeurPedagogique'].includes(role);
            if (!isAuthorizedRole) {
                setCurrentError(`Le rôle '${role}' n'est pas autorisé à gérer les rôles et statuts.`);
            } else if (!schoolIdToFilter) {
                setCurrentError(`Impossible de récupérer les utilisateurs : votre profil n'est pas associé à une école.`);
            }
            setLoadingUsers(false);
            return;
        }

        const usersCollectionRef = collection(db, 'users');
        // La requête va récupérer tous les champs, y compris le nouveau champ 'status' s'il existe
        let usersQuery = query(usersCollectionRef, where('schoolId', '==', schoolIdToFilter), orderBy('email'));

        unsubscribe = onSnapshot(usersQuery, (snapshot) => {
            const fetchedUsers = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(user => user.id !== uid);

            setUsers(fetchedUsers);
            setLoadingUsers(false);
        }, (err) => {
            console.error("UserRoleManagement: Erreur onSnapshot:", err);
            setCurrentError("Échec de la récupération des utilisateurs. Vérifiez votre connexion.");
            setLoadingUsers(false);
        });

        return () => unsubscribe();
    }, [userProfile, selectedSchoolForSuperAdmin]);

    // --- 2. Logique de Changement de Rôle ---
    const handleRoleChange = async (userId, newRole) => {
        if (!userProfile || !['superAdmin', 'admin', 'secretaireGeneral', 'financier', 'directeurPedagogique'].includes(userProfile.role)) {
            setError("Permission refusée: Votre rôle n'est pas autorisé à modifier les rôles.");
            return;
        }

        if (newRole === 'superAdmin' && userProfile.role !== 'superAdmin') {
            setError("Permission refusée: Seul le Super Admin peut attribuer ce rôle.");
            return;
        }

        setUpdatingUserId(userId);
        setError(null);
        setMessage(null);

        try {
            const userDocRef = doc(db, 'users', userId);
            await updateDoc(userDocRef, {
                role: newRole,
            });

            setMessage(`Le rôle a été mis à jour à '${newRole}' avec succès.`);
        } catch (error) {
            console.error("Erreur de mise à jour du rôle:", error);
            setError(`Erreur: Impossible de mettre à jour le rôle. ${error.message}`);
        } finally {
            setUpdatingUserId(null);
        }
    };

    // --- 3. Logique de Suppression d'Utilisateur ---
    const handleUserDelete = async (userId, userEmail) => {
        const allowedRoles = ['superAdmin', 'admin'];
        if (!userProfile || !allowedRoles.includes(userProfile.role)) {
            setError("Permission refusée: Votre rôle n'est pas autorisé à supprimer des utilisateurs.");
            return;
        }

        const userToDelete = users.find(u => u.id === userId);
        if (userToDelete?.role === 'superAdmin' && userProfile.role !== 'superAdmin') {
             setError("Permission refusée: Vous ne pouvez pas supprimer un Super Admin.");
            return;
        }

        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur '${userEmail}' ? Cette action est irréversible et ne supprime pas le compte Firebase Authentication directement.`)) {
            return; // L'utilisateur a annulé
        }

        setDeletingUserId(userId);
        setError(null);
        setMessage(null);

        try {
            const userDocRef = doc(db, 'users', userId);
            await deleteDoc(userDocRef);

            setMessage(`L'utilisateur '${userEmail}' a été supprimé avec succès.`);
        } catch (error) {
            console.error("Erreur de suppression de l'utilisateur:", error);
            setError(`Erreur: Impossible de supprimer l'utilisateur. ${error.message}`);
        } finally {
            setDeletingUserId(null);
        }
    };

    // --- NOUVELLE LOGIQUE POUR LE CHANGEMENT DE STATUT ---
    const handleStatusChange = async (userId, newStatus) => {
        // Les mêmes rôles qui peuvent modifier le rôle devraient pouvoir modifier le statut.
        // Les règles Firestore en backend feront la vérification finale.
        if (!userProfile || !['superAdmin', 'admin', 'secretaireGeneral', 'financier', 'directeurPedagogique'].includes(userProfile.role)) {
            setError("Permission refusée: Votre rôle n'est pas autorisé à modifier le statut.");
            return;
        }

        setUpdatingStatusUserId(userId);
        setError(null);
        setMessage(null);

        try {
            const userDocRef = doc(db, 'users', userId);
            await updateDoc(userDocRef, {
                status: newStatus, // Mise à jour du champ 'status' dans Firestore
            });

            setMessage(`Le statut de l'utilisateur a été mis à jour à '${newStatus}' avec succès.`);
        } catch (error) {
            console.error("Erreur de mise à jour du statut:", error);
            setError(`Erreur: Impossible de mettre à jour le statut. ${error.message}`);
        } finally {
            setUpdatingStatusUserId(null);
        }
    };

    // --- 4. Rendu ---
    if (loadingUsers) {
        return (
            <div className="p-8 text-center">
                <Loader2 className="animate-spin h-8 w-8 text-blue-500 inline-block mr-2" />
                <p className="text-gray-700">Chargement des utilisateurs...</p>
            </div>
        );
    }

    if (currentError) {
        return (
            <div className="p-6 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center justify-center">
                <X size={20} className="mr-3" />
                <p className="font-medium">Erreur: {currentError}</p>
            </div>
        );
    }

    if (users.length === 0) {
        return (
            <div className="p-6 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-lg flex items-center justify-center">
                <User size={20} className="mr-3" />
                <p className="font-medium">Aucun autre utilisateur trouvé pour cette sélection.</p>
            </div>
        );
    }

    return (
        <div className="p-4">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-6 flex items-center">
                <Key size={30} className="mr-3 text-blue-600" /> Gestion des Rôles et Statuts Utilisateur
            </h2>

            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom / Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">UID Auth</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rôle</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th> {/* NOUVELLE COLONNE */}
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modifier Rôle</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {users.map((user) => {
                            const isRoleEditable = user.role !== 'superAdmin' || userProfile.role === 'superAdmin';
                            const isDeletable = isRoleEditable; // Utiliser la même logique pour la suppression que pour le rôle
                            const isStatusSelectEnabled = user.role !== 'superAdmin' || userProfile.role === 'superAdmin'; // Un Super Admin ne peut pas avoir son statut modifié par un Admin d'école, donc le select entier est désactivé

                            const isUpdatingRole = updatingUserId === user.id;
                            const isDeleting = deletingUserId === user.id;
                            const isUpdatingStatus = updatingStatusUserId === user.id; // NOUVELLE VÉRIFICATION

                            // Désactiver les actions si une opération est en cours sur CETTE LIGNE
                            const isDisabledRow = isUpdatingRole || isDeleting || isUpdatingStatus;

                            return (
                                <tr key={user.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        <div className="font-bold">{user.displayName || user.email}</div>
                                        <div className="text-gray-500">{user.email || 'N/A'}</div>
                                    </td>
                                    <td className="px-6 py-4 text-xs font-mono text-gray-600 truncate max-w-xs" title={user.id}>
                                        <div className="flex items-center">
                                            <Fingerprint size={14} className="mr-2 text-red-500 flex-shrink-0" />
                                            <span className="truncate">{user.id}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                            user.role === 'superAdmin' ? 'bg-red-100 text-red-800' :
                                            user.role === 'admin' ? 'bg-indigo-100 text-indigo-800' :
                                            user.role === 'secretaireGeneral' ? 'bg-green-100 text-green-800' :
                                            'bg-gray-100 text-gray-800'
                                        }`}>
                                            {user.role}
                                        </span>
                                    </td>
                                    {/* NOUVELLE CELLULE POUR LE STATUT */}
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {isStatusSelectEnabled ? (
                                            <div className="flex items-center space-x-2">
                                                <select
                                                    // Utilisez 'active' comme valeur par défaut si le statut n'est pas encore défini dans le document Firestore
                                                    defaultValue={user.status || 'active'}
                                                    onChange={(e) => handleStatusChange(user.id, e.target.value)}
                                                    disabled={isDisabledRow} // Le select entier est désactivé si une opération est en cours sur la ligne
                                                    className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50"
                                                >
                                                    {AVAILABLE_STATUSES.map(statusOption => {
                                                        // Condition pour désactiver l'option "active"
                                                        // si l'utilisateur connecté n'est PAS un SuperAdmin
                                                        // ET que le statut actuel de l'utilisateur n'est PAS "active"
                                                        const disableActiveOption = (
                                                            statusOption.value === 'active' &&
                                                            userProfile.role !== 'superAdmin' &&
                                                            user.status !== 'active'
                                                        );

                                                        return (
                                                            <option
                                                                key={statusOption.value}
                                                                value={statusOption.value}
                                                                disabled={disableActiveOption} // Applique la désactivation spécifique
                                                                className={disableActiveOption ? 'text-gray-400' : ''} // Ajoute une classe pour griser visuellement
                                                            >
                                                                {statusOption.label}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                                {isUpdatingStatus && (
                                                    <Loader2 size={20} className="animate-spin text-blue-500" />
                                                )}
                                            </div>
                                        ) : (
                                            // Afficher le statut sans possibilité de modification pour les Super Admins non éditables par d'autres
                                            <span className="text-gray-400">{user.status || 'Non défini'}</span>
                                        )}
                                    </td>
                                    {/* FIN NOUVELLE CELLULE POUR LE STATUT */}
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {isRoleEditable ? (
                                            <div className="flex items-center space-x-2">
                                                <select
                                                    defaultValue={user.role}
                                                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                                    disabled={isDisabledRow}
                                                    className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50"
                                                >
                                                    {AVAILABLE_ROLES.map(role => (
                                                        <option key={role.value} value={role.value}>
                                                            {role.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {isUpdatingRole && (
                                                    <Loader2 size={20} className="animate-spin text-blue-500" />
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-red-500 font-semibold">Super Admin (Non modifiable)</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        {isDeletable ? (
                                            <button
                                                onClick={() => handleUserDelete(user.id, user.email || user.displayName)}
                                                disabled={isDisabledRow}
                                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 transition duration-150"
                                                title={`Supprimer l'utilisateur ${user.email || user.displayName}`}
                                            >
                                                {isDeleting ? (
                                                    <>
                                                        <Loader2 size={16} className="animate-spin mr-1" />
                                                        Supp.
                                                    </>
                                                ) : (
                                                    <>
                                                        <Trash2 size={16} className="mr-1" />
                                                        Supprimer
                                                    </>
                                                )}
                                            </button>
                                        ) : (
                                             <span className="text-gray-400">Non autorisé</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default UserRoleManagement;
